import type {
  DocumentDetail,
  DocumentInput,
  DocumentListQuery,
  DocumentSummaryRow,
  Paginated,
} from '@facture/contracts'
import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { printPdfBlob } from './print'

const IN_FLIGHT = new Set(['pending', 'sent', 'processing'])

export const documentsQuery = (query: DocumentListQuery) =>
  queryOptions({
    queryKey: ['documents', 'list', query],
    queryFn: ({ signal }) =>
      api.get<Paginated<DocumentSummaryRow>>('/documents', { query: { ...query }, signal }),
    placeholderData: keepPreviousData,
    // Mientras haya comprobantes viajando a Hacienda, la lista se refresca sola.
    refetchInterval: (q) => (q.state.data?.items.some((d) => IN_FLIGHT.has(d.haciendaStatus)) ? 4000 : false),
  })

export const documentQuery = (id: string) =>
  queryOptions({
    queryKey: ['documents', 'detail', id],
    queryFn: () => api.get<DocumentDetail>(`/documents/${id}`),
    refetchInterval: (q) => (q.state.data && IN_FLIGHT.has(q.state.data.haciendaStatus) ? 3000 : false),
  })

export function useSaveDocument() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, values }: { id?: string; values: DocumentInput }) =>
      id
        ? api.put<DocumentDetail>(`/documents/${id}`, values)
        : api.post<DocumentDetail>('/documents', values),
    onSuccess: (doc) => {
      queryClient.setQueryData(documentQuery(doc.id).queryKey, doc)
      queryClient.invalidateQueries({ queryKey: ['documents', 'list'] })
      queryClient.invalidateQueries({ queryKey: ['customers'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useDocumentAction() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'issue' | 'retry' }) =>
      api.post<DocumentDetail>(`/documents/${id}/${action}`),
    onSuccess: (doc) => {
      queryClient.setQueryData(documentQuery(doc.id).queryKey, doc)
      queryClient.invalidateQueries({ queryKey: ['documents', 'list'] })
    },
  })
}

/** Descarga el PDF y lanza el diálogo de impresión del navegador (ver `print.ts`). */
export async function printPdf(id: string, format: 'letter' | 'pos' = 'letter') {
  // `pos`: tiquete térmico de 80 mm; sin él, hoja carta.
  const blob = await api.get<Blob>(`/documents/${id}/pdf`, format === 'pos' ? { query: { format } } : {})
  await printPdfBlob(blob)
}

/** Descarga un archivo autenticado (XML, PDF) sin exponer el token en la URL. */
export async function downloadFile(
  path: string,
  filename: string,
  onResponse?: (response: Response) => void,
) {
  const blob = await api.get<Blob>(path, { onResponse })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
